/**
 * Edit Bid/Budget modal — opened from Campaigns BulkActions.
 *
 * Design source: oneads-remix/project/bulk-editor-popup.html (Claude
 * Design handoff). Recreates the layout 1:1: 220px branded sidebar
 * with nav-items + count pills, right pane split into header / body /
 * footer with an accent-50 footer panel.
 *
 * Tabs are always visible; tile / hint / Apply visibility per-tab is
 * driven by the bidding_strategy mix of the selected campaigns:
 *   - all Max Conv:    Campaign tab → Target CPA + Daily Budget;
 *                       Ad Group / Keyword bodies show an empty state.
 *   - all non-Max:     Campaign tab → Daily Budget only;
 *                       Ad Group → CPA Goal + CPT Bid (sub-toggles);
 *                       Keyword → CPT Bid.
 *   - mixed:           Campaign tab → Daily Budget only;
 *                       Ad Group / Keyword empty (cross-type-unsafe).
 *
 * Apply is fire-and-forget — parent owns the mutation lifecycle and
 * the global toast.
 */

import { useEffect, useMemo, useState } from "react";
import { useDiscardGuard } from "@/lib/discard-guard";
import type { ReactNode } from "react";
import { I } from "@/components/Icon";
import {
  useAdGroupsCountForCampaigns,
  useKeywordsCountForCampaigns,
  type BulkEditAction,
  type BulkEditField,
  type BulkEditLevel,
  type BulkEditRequest,
  type BulkEditUnit,
} from "@/lib/api-hooks";
import { adsFiltersToStatsFilters } from "@/lib/ads-filters";
import type { AdsFilters } from "@/lib/ads-filters";

type SelectionMode = "max_conv_only" | "non_max_only" | "mixed" | "empty";

function classifySelection(
  campaigns: Array<{ campaign_id: number; bidding_strategy?: string | null }>
): SelectionMode {
  if (!campaigns.length) return "empty";
  const hasMaxConv = campaigns.some(
    (c) => c.bidding_strategy === "MAX_CONVERSIONS"
  );
  const hasNonMaxConv = campaigns.some(
    (c) => c.bidding_strategy !== "MAX_CONVERSIONS"
  );
  if (hasMaxConv && hasNonMaxConv) return "mixed";
  if (hasMaxConv) return "max_conv_only";
  return "non_max_only";
}

// ---------------------------------------------------------------------------
// Inline icons used by the tiles + segmented control. Kept local so we don't
// pollute the global Icon registry with one-off shapes.
// ---------------------------------------------------------------------------

const Svg = ({
  size = 16,
  children,
  strokeWidth = 1.75,
}: {
  size?: number;
  children: ReactNode;
  strokeWidth?: number;
}) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

const ICONS = {
  target: (
    <Svg>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
    </Svg>
  ),
  dollarBox: (
    <Svg>
      <rect x="3" y="4" width="18" height="16" rx="2.5" />
      <path d="M14.5 9.5c-.6-.7-1.5-1-2.5-1-1.5 0-2.5.7-2.5 1.8 0 1.1.9 1.5 2.5 1.7 1.7.2 2.7.7 2.7 1.9 0 1.2-1 2-2.7 2-1.1 0-2-.4-2.5-1.1" />
      <path d="M12 7.5v9" />
    </Svg>
  ),
  info: (
    <Svg size={14}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5" />
      <circle cx="12" cy="8" r=".7" fill="currentColor" />
    </Svg>
  ),
  arrowUp: (
    <Svg size={14}>
      <path d="M12 19V5" />
      <path d="m5 12 7-7 7 7" />
    </Svg>
  ),
  arrowDown: (
    <Svg size={14}>
      <path d="M12 5v14" />
      <path d="m5 12 7 7 7-7" />
    </Svg>
  ),
  switchOff: (
    <Svg size={14}>
      <path d="M18 6 6 18" />
      <path d="M6 6l12 12" />
    </Svg>
  ),
  check: (
    <Svg size={12} strokeWidth={3}>
      <path d="m5 12 5 5 10-10" />
    </Svg>
  ),
  megaphone: (
    <Svg>
      <path d="M3 11v2a2 2 0 0 0 2 2h1l3 4h2v-14h-2l-3 4H5a2 2 0 0 0-2 2z" />
      <path d="M14 7a5 5 0 0 1 0 10" />
      <path d="M18 5a8 8 0 0 1 0 14" />
    </Svg>
  ),
  stack: (
    <Svg>
      <path d="m12 3 9 5-9 5-9-5 9-5z" />
      <path d="m3 13 9 5 9-5" />
      <path d="m3 18 9 5 9-5" />
    </Svg>
  ),
  key: (
    <Svg>
      <circle cx="8" cy="15" r="3" />
      <path d="M10.5 12.5 21 2" />
      <path d="m16 7 3 3" />
      <path d="m18 5 3 3" />
    </Svg>
  ),
};

// ---------------------------------------------------------------------------
// Primitive UI pieces (tile, segmented control, radio card, check card).
// Style comes from the global .bk-* rules in app.css.
// ---------------------------------------------------------------------------

function Tile({
  icon,
  label,
  active,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={"bk-tile " + (active ? "active" : "")}
      onClick={onClick}
    >
      <span className="bk-tile-ico">{icon}</span>
      <span className="bk-tile-lbl">{label}</span>
    </button>
  );
}

type SegOption = {
  value: string;
  label: string;
  ico?: ReactNode;
};

function Segmented({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: SegOption[];
}) {
  return (
    <div className="bk-seg" role="tablist">
      {options.map((o) => (
        <button
          key={o.value}
          role="tab"
          type="button"
          className={"bk-seg-btn " + (o.value === value ? "active" : "")}
          onClick={() => onChange(o.value)}
        >
          {o.ico && <span className="bk-seg-ico">{o.ico}</span>}
          {o.label}
        </button>
      ))}
    </div>
  );
}

function RadioCard({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={"bk-rcard " + (active ? "active" : "")}
      onClick={onClick}
    >
      <span className={"bk-radio " + (active ? "checked" : "")}>
        <span className="bk-radio-dot" />
      </span>
      <span>{label}</span>
    </button>
  );
}

function CheckCard({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={"bk-ccard " + (active ? "active" : "")}
      onClick={onClick}
    >
      <span className={"bk-check " + (active ? "checked" : "")}>
        {active && ICONS.check}
      </span>
      <span>{label}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

const TABS: Array<{ id: BulkEditLevel; label: string; ico: ReactNode }> = [
  { id: "campaign", label: "Campaign", ico: ICONS.megaphone },
  { id: "ad_group", label: "Ad Group", ico: ICONS.stack },
  { id: "keyword", label: "Keyword", ico: ICONS.key },
];

const ENTITY_NOUN: Record<BulkEditLevel, [string, string]> = {
  campaign: ["campaign", "campaigns"],
  ad_group: ["ad group", "ad groups"],
  keyword: ["keyword", "keywords"],
};

export function EditBidBudgetModal({
  open,
  onClose: onCloseProp,
  selectedCampaigns,
  filters,
  onApply,
  restrictLevel,
  scopeAdGroupIds,
  scopeKeywordIds,
  scopeCount,
}: {
  open: boolean;
  onClose: () => void;
  selectedCampaigns: Array<{
    campaign_id: number;
    bidding_strategy?: string | null;
  }>;
  /** AdsFilters from the page — used to scope the count probes (date
   *  range + app + country narrow the ad-group / keyword counts to
   *  the same dataset the user sees in the Campaigns table). */
  filters: AdsFilters;
  onApply: (request: BulkEditRequest) => void;
  /** In-campaign "Edit bids": pin the modal to ONE level (no Campaign
   *  tab, no level switching) and scope the edit to the selected
   *  entities. Omit for the campaign-level Edit Bid/Budget flow. */
  restrictLevel?: BulkEditLevel;
  scopeAdGroupIds?: number[];
  scopeKeywordIds?: number[];
  /** Selected-entity count for the subline + nav pill in restrict mode
   *  (the count probes are disabled there). */
  scopeCount?: number;
}) {
  const mode = useMemo(
    () => classifySelection(selectedCampaigns),
    [selectedCampaigns]
  );
  const campaignIds = useMemo(
    () => selectedCampaigns.map((c) => c.campaign_id),
    [selectedCampaigns]
  );
  const statsFilters = useMemo(
    () => adsFiltersToStatsFilters(filters),
    [filters]
  );

  // In restrict mode the count is the selected-entity count, so skip the
  // campaign-wide count probes entirely.
  const probesEnabled = open && !restrictLevel;
  const adGroupCountQuery = useAdGroupsCountForCampaigns(
    campaignIds,
    statsFilters,
    probesEnabled
  );
  const keywordCountQuery = useKeywordsCountForCampaigns(
    campaignIds,
    statsFilters,
    probesEnabled
  );

  const counts: Record<BulkEditLevel, number | null> = restrictLevel
    ? {
        campaign: campaignIds.length,
        ad_group: restrictLevel === "ad_group" ? scopeCount ?? 0 : null,
        keyword: restrictLevel === "keyword" ? scopeCount ?? 0 : null,
      }
    : {
        campaign: campaignIds.length,
        ad_group: adGroupCountQuery.data ?? null,
        keyword: keywordCountQuery.data ?? null,
      };

  const [tab, setTab] = useState<BulkEditLevel>(restrictLevel ?? "campaign");
  const [campaignField, setCampaignField] =
    useState<BulkEditField>("daily_budget");
  const [agField, setAgField] = useState<"cpa_goal" | "cpt_bid">("cpa_goal");
  // CPT Bid at the ad_group tab fans out across TWO ASA entities:
  //   - "Default Max CPT Bid"  → asa_adgroup_meta.default_bid (the ad_group-
  //     level Max CPT bid every ad_group in a non-Max-Conv campaign carries)
  //   - "CPT Bid"              → asa_keyword_meta.bid (the per-keyword CPT
  //     bid inside the ad_groups under the selected campaigns)
  // Each is a different ASA entity-level edit, so when both are ticked the
  // modal fires two separate POST /bulk-edit-bid-budget requests on Apply.
  const [agCptDefault, setAgCptDefault] = useState(true);
  const [agCptCpc, setAgCptCpc] = useState(false);

  const [action, setAction] = useState<BulkEditAction>("increase");
  const [byMode, setByMode] = useState<BulkEditUnit>("percent");
  const [valueStr, setValueStr] = useState("");

  // Reset on open / mode change so a stale form doesn't bleed across
  // selection changes.
  useEffect(() => {
    if (!open) return;
    setTab(restrictLevel ?? "campaign");
    setCampaignField(mode === "max_conv_only" ? "target_cpa" : "daily_budget");
    setAgField("cpa_goal");
    setAgCptDefault(true);
    setAgCptCpc(false);
    setAction("increase");
    setByMode("percent");
    setValueStr("");
  }, [open, mode, restrictLevel]);

  const guard = useDiscardGuard(open, valueStr.trim() !== "", onCloseProp);
  const onClose = guard.requestClose;

  // ---------- Per-tab visibility / available fields ----------
  // (Hook order must be stable, so keep this *above* the open-guard
  // early return.)

  const visibleCampaignFields: BulkEditField[] = useMemo(() => {
    if (mode === "max_conv_only") return ["target_cpa", "daily_budget"];
    if (mode === "non_max_only") return ["daily_budget"];
    if (mode === "mixed") return ["daily_budget"];
    return [];
  }, [mode]);

  if (!open) return null;

  const adGroupHasContent = mode === "non_max_only";
  const keywordHasContent = mode === "non_max_only";

  // ---------- Compute fieldsToSend + can-apply ----------

  // Build the (1 or 2) request payloads to fire on Apply. Most cases
  // are 1 request; the ad_group / CPT Bid case can fan out to TWO
  // levels (ad_group default_bid + keyword bid) because the user
  // ticked both sub-checkboxes — these are different ASA entity
  // levels, so each gets its own POST.
  const buildRequests = (): BulkEditRequest[] => {
    // Normalise the `,` decimal separator to `.` before parsing so
    // `1,7` (RU/EU keyboard) and `1.7` (en-US) become the same number.
    // Backend has the same normalisation as defence in depth.
    const numericValue =
      action === "switch_off" ? null : Number(valueStr.replace(",", "."));
    // In restrict mode, scope each request to the selected entities:
    //   - ad_group level → ad_group_ids;
    //   - keyword level  → keyword_ids (direct keyword selection) OR
    //     ad_group_ids (the ad_group-tab CPT-Bid cascade to keywords
    //     under the selected ad_groups).
    const scopeFor = (
      level: BulkEditLevel
    ): Pick<BulkEditRequest, "ad_group_ids" | "keyword_ids"> => {
      if (!restrictLevel) return {};
      if (level === "ad_group" && scopeAdGroupIds)
        return { ad_group_ids: scopeAdGroupIds };
      if (level === "keyword") {
        if (scopeKeywordIds) return { keyword_ids: scopeKeywordIds };
        if (scopeAdGroupIds) return { ad_group_ids: scopeAdGroupIds };
      }
      return {};
    };
    const baseFields = (level: BulkEditLevel, fields: BulkEditField[]):
      BulkEditRequest => ({
      campaign_ids: campaignIds,
      level,
      fields,
      action,
      value: numericValue,
      value_unit: action === "set_new" ? "amount" : byMode,
      ...scopeFor(level),
    });
    if (tab === "campaign") {
      return [baseFields("campaign", [campaignField])];
    }
    if (tab === "ad_group") {
      if (!adGroupHasContent) return [];
      if (agField === "cpa_goal") {
        return [baseFields("ad_group", ["cpa_goal"])];
      }
      // CPT Bid: fan out per ticked sub-checkbox.
      const reqs: BulkEditRequest[] = [];
      if (agCptDefault) reqs.push(baseFields("ad_group", ["default_bid"]));
      if (agCptCpc)     reqs.push(baseFields("keyword",  ["bid"]));
      return reqs;
    }
    return keywordHasContent ? [baseFields("keyword", ["bid"])] : [];
  };

  const requestsPreview = buildRequests();
  const fieldsToSend: BulkEditField[] = requestsPreview.flatMap(
    (r) => r.fields
  );

  const isSwitchOff = action === "switch_off";
  const allowSwitchOff = tab === "ad_group" && agField === "cpa_goal";
  const showByMode = action !== "set_new" && action !== "switch_off";
  const showValueInput = action !== "switch_off";
  const effectiveUnit: BulkEditUnit =
    action === "set_new" ? "amount" : byMode;

  const canApply = (() => {
    if (fieldsToSend.length === 0) return false;
    if (isSwitchOff) return true;
    const v = Number(valueStr.replace(",", "."));
    return !!valueStr && !Number.isNaN(v) && v > 0;
  })();

  // ---------- Action options for the segmented control ----------

  const actionOptions: SegOption[] = [
    { value: "increase", label: "Increase", ico: ICONS.arrowUp },
    { value: "decrease", label: "Decrease", ico: ICONS.arrowDown },
    { value: "set_new", label: "Set new" },
    ...(allowSwitchOff
      ? [
          {
            value: "switch_off",
            label: "Switch off",
            ico: ICONS.switchOff,
          },
        ]
      : []),
  ];

  // ---------- Handlers ----------

  const handleApply = () => {
    if (!canApply) return;
    // Fire each request the modal built (1 for most cases; 2 when on
    // ad_group / CPT Bid with both sub-checkboxes ticked — fanout to
    // ad_group default_bid + keyword bid in parallel). Parent owns
    // the mutation and fires a toast per resolved request.
    for (const req of buildRequests()) {
      onApply(req);
    }
    setValueStr("");
    setAction("increase");
    setByMode("percent");
  };

  // ---------- Header copy ----------

  const titleMap: Record<BulkEditLevel, string> = {
    campaign: "Bulk Edit: Campaigns",
    ad_group: "Bulk Edit: Ad Groups",
    keyword: "Bulk Edit: Keywords",
  };
  const tabCount = counts[tab];
  const [singular, plural] = ENTITY_NOUN[tab];
  const subline =
    tabCount === null
      ? `Loading ${plural}…`
      : `Apply changes to ${tabCount} selected ${tabCount === 1 ? singular : plural}.`;

  // ---------- Action-row label ----------

  const actionLabel = (() => {
    if (action === "increase") return "INCREASE BY";
    if (action === "decrease") return "DECREASE BY";
    if (action === "set_new") return "SET NEW VALUE";
    return "CONFIRM";
  })();

  return (
    <div
      className="bk-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bk-modal" role="dialog" aria-modal="true">
        <button className="bk-close" aria-label="Close" onClick={onClose}>
          {I.x}
        </button>

        <aside className="bk-side">
          <div className="bk-brand">
            Bulk <em>Editor</em>
          </div>
          <nav className="bk-nav">
            {(restrictLevel
              ? TABS.filter((t) => t.id === restrictLevel)
              : TABS
            ).map((t) => {
              const active = tab === t.id;
              const c = counts[t.id];
              return (
                <button
                  key={t.id}
                  type="button"
                  className={"bk-nav-item " + (active ? "active" : "")}
                  onClick={() => setTab(t.id)}
                >
                  <span className="bk-nav-ico">{t.ico}</span>
                  <span className="bk-nav-lbl">{t.label}</span>
                  <span className="bk-nav-count">
                    {c === null ? "…" : c}
                  </span>
                </button>
              );
            })}
          </nav>
        </aside>

        <div className="bk-main">
          <header className="bk-head">
            <h2>{titleMap[tab]}</h2>
            <p>{subline}</p>
          </header>

          <div className="bk-body">
            {tab === "campaign" && visibleCampaignFields.length > 0 && (
              <CampaignBody
                visibleFields={visibleCampaignFields}
                campaignField={campaignField}
                setCampaignField={setCampaignField}
              />
            )}
            {tab === "ad_group" && adGroupHasContent && (
              <AdGroupBody
                agField={agField}
                setAgField={setAgField}
                agCptDefault={agCptDefault}
                agCptCpc={agCptCpc}
                onToggleDefault={() => {
                  // Don't allow unticking the last remaining sub-target.
                  if (agCptDefault && !agCptCpc) return;
                  setAgCptDefault((v) => !v);
                }}
                onToggleCpc={() => {
                  if (agCptCpc && !agCptDefault) return;
                  setAgCptCpc((v) => !v);
                }}
              />
            )}
            {tab === "keyword" && keywordHasContent && <KeywordBody />}

            {((tab === "ad_group" && !adGroupHasContent) ||
              (tab === "keyword" && !keywordHasContent)) && (
              <EmptyTabHint tab={tab} mode={mode} />
            )}

            {/* Spacer pushes the Action block to the bottom of the body
                — keeps the chrome anchored regardless of how many
                tiles / sub-toggles are visible above. */}
            <div className="bk-mid-spacer" />

            {fieldsToSend.length > 0 && (
              <div className="bk-bottom">
                <div className="bk-section-lbl">ACTION</div>
                <Segmented
                  value={action}
                  onChange={(v) => setAction(v as BulkEditAction)}
                  options={actionOptions}
                />
                <div
                  style={{
                    visibility: showValueInput ? "visible" : "hidden",
                  }}
                  aria-hidden={!showValueInput}
                >
                  <div className="bk-section-lbl" style={{ marginTop: 22 }}>
                    {actionLabel}
                  </div>
                  <div
                    className="bk-row"
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 10,
                      visibility: showByMode ? "visible" : "hidden",
                    }}
                  >
                    <RadioCard
                      label="By percent %"
                      active={byMode === "percent"}
                      onClick={() => setByMode("percent")}
                    />
                    <RadioCard
                      label="By amount $"
                      active={byMode === "amount"}
                      onClick={() => setByMode("amount")}
                    />
                  </div>
                  <div className="bk-input">
                    <span className="bk-input-prefix">
                      {action === "set_new"
                        ? "$"
                        : effectiveUnit === "percent"
                        ? "%"
                        : "$"}
                    </span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={valueStr}
                      onChange={(e) => {
                        // Accept either `,` or `.` as the decimal
                        // separator — RU/EU keyboards put `,` on the
                        // numpad. We keep whatever the user typed in
                        // the input (so the cursor / IME stay sane),
                        // and normalise to `.` only when we send.
                        const v = e.target.value;
                        if (v === "" || /^\d*[.,]?\d*$/.test(v)) {
                          setValueStr(v);
                        }
                      }}
                      placeholder={
                        action === "set_new"
                          ? "Enter new value"
                          : effectiveUnit === "percent"
                          ? "Enter percentage"
                          : "Enter amount"
                      }
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <footer className="bk-foot">
            <button
              className="bk-apply"
              onClick={handleApply}
              disabled={!canApply}
            >
              Apply changes
            </button>
          </footer>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-tab body content
// ---------------------------------------------------------------------------

function CampaignBody({
  visibleFields,
  campaignField,
  setCampaignField,
}: {
  visibleFields: BulkEditField[];
  campaignField: BulkEditField;
  setCampaignField: (f: BulkEditField) => void;
}) {
  const tileFor = (f: BulkEditField) => {
    if (f === "target_cpa") {
      return (
        <Tile
          key="target_cpa"
          icon={ICONS.target}
          label="Target CPA"
          active={campaignField === "target_cpa"}
          onClick={() => setCampaignField("target_cpa")}
        />
      );
    }
    return (
      <Tile
        key="daily_budget"
        icon={ICONS.dollarBox}
        label="Daily Budget"
        active={campaignField === "daily_budget"}
        onClick={() => setCampaignField("daily_budget")}
      />
    );
  };
  const hint =
    campaignField === "target_cpa"
      ? "Target CPA is only applied to Max Conversion campaigns."
      : "Daily Budget applies to all campaign types.";
  return (
    <div className="bk-top">
      <div className="bk-section-lbl">SETTING TO EDIT</div>
      <div
        className={
          "bk-tiles" + (visibleFields.length === 1 ? " single" : "")
        }
      >
        {visibleFields.map(tileFor)}
      </div>
      <div className="bk-hint">
        {ICONS.info}
        <span>{hint}</span>
      </div>
    </div>
  );
}

function AdGroupBody({
  agField,
  setAgField,
  agCptDefault,
  agCptCpc,
  onToggleDefault,
  onToggleCpc,
}: {
  agField: "cpa_goal" | "cpt_bid";
  setAgField: (f: "cpa_goal" | "cpt_bid") => void;
  agCptDefault: boolean;
  agCptCpc: boolean;
  onToggleDefault: () => void;
  onToggleCpc: () => void;
}) {
  return (
    <>
      <div className="bk-top">
        <div className="bk-section-lbl">SETTING TO EDIT</div>
        <div className="bk-tiles">
          <Tile
            icon={ICONS.target}
            label="CPA Goal"
            active={agField === "cpa_goal"}
            onClick={() => setAgField("cpa_goal")}
          />
          <Tile
            icon={ICONS.dollarBox}
            label="CPT Bid"
            active={agField === "cpt_bid"}
            onClick={() => setAgField("cpt_bid")}
          />
        </div>
        <div className="bk-hint">
          {ICONS.info}
          <span>
            Available for all ad groups except those in Max Conversion
            campaigns.
          </span>
        </div>
      </div>
      <div
        className="bk-mid"
        style={{ visibility: agField === "cpt_bid" ? "visible" : "hidden" }}
        aria-hidden={agField !== "cpt_bid"}
      >
        <div className="bk-section-lbl">
          BID TYPE{" "}
          <span className="bk-section-hint">choose one or both</span>
        </div>
        <div className="bk-checks">
          {/* Default Max CPT Bid → ad_group's default_bid (per ASA's
              defaultBidAmount). CPT Bid → keyword.bid (per-keyword
              bidAmount inside the ad_groups). Both checkable. */}
          <CheckCard
            label="Default Max CPT Bid"
            active={agCptDefault}
            onClick={onToggleDefault}
          />
          <CheckCard
            label="CPT Bid"
            active={agCptCpc}
            onClick={onToggleCpc}
          />
        </div>
      </div>
    </>
  );
}

function KeywordBody() {
  return (
    <div className="bk-top">
      <div className="bk-section-lbl">SETTING TO EDIT</div>
      <div className="bk-tiles single">
        <Tile
          icon={ICONS.dollarBox}
          label="CPT Bid"
          active
          onClick={() => {
            /* single option, no-op */
          }}
        />
      </div>
      <div className="bk-hint">
        {ICONS.info}
        <span>Adjust CPT Bid for the selected keywords.</span>
      </div>
    </div>
  );
}

function EmptyTabHint({
  tab,
  mode,
}: {
  tab: BulkEditLevel;
  mode: SelectionMode;
}) {
  const noun = tab === "ad_group" ? "Ad Group" : "Keyword";
  let reason: string;
  if (mode === "max_conv_only") {
    reason = `${noun} bids aren't editable on Max Conversions campaigns. Switch to the Campaign tab to edit Target CPA / Daily Budget.`;
  } else if (mode === "mixed") {
    reason = `Your selection mixes Max Conversions with manual-bid campaigns. ${noun}-level edits don't apply to Max Conversions, so this tab is unavailable. Use the Campaign tab to edit Daily Budget across all selected campaigns.`;
  } else {
    reason = "";
  }
  return (
    <div className="bk-empty">
      {ICONS.info}
      <span>{reason}</span>
    </div>
  );
}
